import React, { useState, useEffect, useRef, useMemo } from 'react'
import './trade-portal.styles.scss'
import { getQueryFromFirestore } from '@/utils/getDataFromDbSimplified'
import { useSelector } from 'react-redux'
import TradePortalPostCard from './trade-portal-post-card.component'
import TradePortalPostModal from './trade-portal-post-modal.component'
import TradePortalDetailModal from './trade-portal-detail-modal.component'
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component'

const TradePortal = ({ isManager, gameData, playerData, gameElements }) => {
  
  const screenSize = useSelector((state) => state.contentData.screenSize)
  const [tradePosts, setTradePosts] = useState({})
  const [activeTab, setActiveTab] = useState(isManager ? 'pending' : 'browse')
  const [selectedPostId, setSelectedPostId] = useState(null)
  const [showPostModal, setShowPostModal] = useState(false)
  const [loading, setLoading] = useState(true)

  const listenerRef = useRef(null)
  const tradePostsRef = useRef({})

  useEffect(() => {
    if (!gameData?.gameId) return
    tradePostsRef.current = {}
    setTradePosts({})
    loadTradePosts()
    return () => {
      if (listenerRef.current) listenerRef.current()
    }
  }, [gameData?.gameId])

  function loadTradePosts() {
    getQueryFromFirestore({
      path: `games/${gameData.gameId}/tradePortal`,
      callback: (data, docId) => {
        tradePostsRef.current = { ...tradePostsRef.current, [docId]: { ...data, id: docId } }
        setTradePosts({ ...tradePostsRef.current })
      },
      handleListener: (unsub) => {
        listenerRef.current = unsub
        setLoading(false)
      },
    })
  }

  const postList = Object.values(tradePosts)

  const browsePosts = postList
    .filter((p) => p.status === 'pending' && p.posterId !== playerData?.playerId)
    .sort((a, b) => (b.ts_created?.seconds ?? 0) - (a.ts_created?.seconds ?? 0))

  const myPosts = postList
    .filter((p) => p.posterId === playerData?.playerId)
    .sort((a, b) => (b.ts_created?.seconds ?? 0) - (a.ts_created?.seconds ?? 0))

  const pendingPosts = postList
    .filter((p) => ['pending', 'awaiting_manager'].includes(p.status))
    .sort((a, b) => (b.ts_created?.seconds ?? 0) - (a.ts_created?.seconds ?? 0))

  const completedPosts = postList
    .filter((p) => ['completed', 'reversed', 'cancelled'].includes(p.status))
    .sort((a, b) => (b.ts_created?.seconds ?? 0) - (a.ts_created?.seconds ?? 0))

  const myPostsWithNewOffers = myPosts.filter((p) =>
    Object.values(p.offers ?? {}).some((o) => o.status === 'pending')
  ).length

  function getDisplayedPosts() {
    if (isManager) return activeTab === 'pending' ? pendingPosts : completedPosts
    return activeTab === 'browse' ? browsePosts : myPosts
  }

  const displayedPosts = getDisplayedPosts()
  const selectedPost = selectedPostId ? tradePosts[selectedPostId] ?? null : null

  return (
    <div className={`trade-portal ${screenSize}`}>
      {
        (!isManager && gameData.access.includes('tradePortal')) &&
        <div className='trade-portal-header'>
          <div></div>
          {!isManager && (
            <button className='g-button med-btn' onClick={() => setShowPostModal(true)}>
              + New Trade
            </button>
          )}
        </div>
      }

      <div className='trade-portal-tabs'>
        {isManager ? (
          <>
            <button
              className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
              onClick={() => setActiveTab('pending')}
            >
              Pending
              {pendingPosts.length > 0 && (
                <span className='tab-badge'>{pendingPosts.length}</span>
              )}
            </button>
            <button
              className={`tab-btn ${activeTab === 'completed' ? 'active' : ''}`}
              onClick={() => setActiveTab('completed')}
            >
              Completed
            </button>
          </>
        ) : (
          <>
            <button
              className={`tab-btn ${activeTab === 'browse' ? 'active' : ''}`}
              onClick={() => setActiveTab('browse')}
            >
              Browse
              {browsePosts.length > 0 && (
                <span className='tab-badge'>{browsePosts.length}</span>
              )}
            </button>
            <button
              className={`tab-btn ${activeTab === 'my-posts' ? 'active' : ''}`}
              onClick={() => setActiveTab('my-posts')}
            >
              My Posts
              {myPostsWithNewOffers > 0 && (
                <span className='tab-badge'>{myPostsWithNewOffers}</span>
              )}
            </button>
          </>
        )}
      </div>
      <div className="g-space-1"></div>

      {loading ? (
        <div className='trade-portal-empty'>
          <ThreeDotsLoader />
        </div>
      ) : displayedPosts.length === 0 ? (
        <div className='trade-portal-empty'>
          {activeTab === 'browse' && 'No trades available right now.'}
          {activeTab === 'my-posts' && "You haven't posted any trades yet."}
          {activeTab === 'pending' && 'No pending trades.'}
          {activeTab === 'completed' && 'No completed trades yet.'}
        </div>
      ) : (
        <div className='trade-portal-list'>
          {displayedPosts.map((post) => (
            <TradePortalPostCard
              key={post.id}
              post={post}
              gameElements={gameElements}
              isOwn={post.posterId === playerData?.playerId}
              onClick={() => setSelectedPostId(post.id)}
            />
          ))}
        </div>
      )}

      {showPostModal && (
        <TradePortalPostModal
          show={showPostModal}
          cancel={() => setShowPostModal(false)}
          gameData={gameData}
          playerData={playerData}
          gameElements={gameElements}
        />
      )}

      {selectedPost && (
        <TradePortalDetailModal
          show={true}
          cancel={() => setSelectedPostId(null)}
          post={selectedPost}
          gameData={gameData}
          playerData={playerData}
          gameElements={gameElements}
          isManager={isManager}
        />
      )}
    </div>
  )
}

export default TradePortal
